import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X, User, Image as ImageIcon, Send, Edit3, Tag, Type, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { API_URL } from '../config';



const getCategoryColor = (category) => {
  switch (category) {
    case 'Wildlife': return { bg: 'rgba(234, 88, 12, 0.2)', text: '#ea580c' };
    case 'Trekking': return { bg: 'rgba(234, 88, 12, 0.2)', text: '#ea580c' };
    case 'Nature': return { bg: 'rgba(234, 88, 12, 0.2)', text: '#ea580c' };
    case 'Adventure': return { bg: 'rgba(234, 88, 12, 0.2)', text: '#ea580c' };
    default: return { bg: 'rgba(234, 88, 12, 0.2)', text: '#ea580c' };
  }
};

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '', category: 'Adventure', excerpt: '', fullStory: '', author: '', date: new Date().toISOString().split('T')[0], image: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, image: reader.result }));
      setUploadingImage(false);
    };
    reader.onerror = () => {
      toast.error("Failed to read file");
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };
  
  const fileInputRef = useRef(null);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) {
      toast.error("Please upload an image.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('Your story has been submitted and is pending admin approval!');
        setShowForm(false);
        setFormData({ title: '', category: 'Adventure', excerpt: '', fullStory: '', author: '', date: new Date().toISOString().split('T')[0], image: '' });
      } else {
        toast.error('Failed to submit story');
      }
    } catch (err) {
      toast.error('Network error during submission');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetch(`${API_URL}/api/stories`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setStories(data);
        } else {
          console.error("Backend returned invalid format:", data);
        }
      })
      .catch(err => console.error("Error fetching stories:", err));
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (selectedStory || showForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedStory, showForm]);

  return (
    <div className="container section">
      <h1 className="section-title">Travel Stories</h1>
      <p className="section-subtitle">
        Immerse yourself in incredible experiences and adventures shared by fellow travelers.
      </p>
      
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '1rem 2rem', backgroundColor: '#ea580c', color: 'white', border: 'none', borderRadius: '30px',
            fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,95,21,0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,95,21,0.4)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,95,21,0.3)'; }}
        >
          Share Your Story
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '2.5rem',
        marginTop: '3rem'
      }}>
        {stories.map((story, index) => {
          const colors = getCategoryColor(story.category);

          return (
            <motion.div
              key={story.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              style={{
                backgroundColor: '#fdfdfc',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 15px rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #eaeaea'
              }}
            >
              {/* Image Section */}
              <div style={{ height: '220px', position: 'relative', overflow: 'hidden' }}>
                <img
                  src={story.image}
                  alt={story.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <span style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  backgroundColor: colors.bg,
                  backdropFilter: 'blur(4px)',
                  color: colors.text,
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  letterSpacing: '0.5px'
                }}>
                  {story.category}
                </span>
              </div>

              {/* Content Section */}
              <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#111', margin: '0 0 1rem 0', lineHeight: '1.3' }}>
                  {story.title}
                </h3>

                <p style={{ color: '#5f6368', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 1.5rem 0' }}>
                  {story.excerpt}
                </p>

                {/* Divider */}
                <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0 0 1.25rem 0' }} />

                {/* Footer Row */}
                <div style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                  <span>{story.author}</span>
                  <span style={{ margin: '0 0.5rem' }}>•</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Calendar size={14} />
                    <span>{story.date}</span>
                  </div>
                </div>

                {/* Spacer to push button to bottom */}
                <div style={{ flexGrow: 1 }}></div>

                <button
                  onClick={() => setSelectedStory(story)}
                  style={{
                    width: '100%',
                    padding: '0.85rem',
                    backgroundColor: '#fed7aa', // Light peach/orange color
                    color: '#111',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.05rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s, transform 0.1s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#fdbf85';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#fed7aa';
                  }}
                  onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                  onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  Read Story
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Modal / Frame Overlay */}
      <AnimatePresence>
        {selectedStory && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem'
          }}>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedStory(null)}
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                backdropFilter: 'blur(6px)'
              }}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                backgroundColor: '#fffdfa',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '750px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                zIndex: 10000,
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <button
                onClick={() => setSelectedStory(null)}
                style={{
                  position: 'absolute',
                  top: '1.2rem',
                  right: '1.2rem',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.5)'}
              >
                <X size={20} />
              </button>

              <div style={{ height: '350px', width: '100%', flexShrink: 0 }}>
                <img src={selectedStory.image} alt={selectedStory.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>

              <div style={{ padding: '2.5rem', maxWidth: '650px', margin: '0 auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <span style={{
                    backgroundColor: getCategoryColor(selectedStory.category).bg,
                    color: getCategoryColor(selectedStory.category).text,
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                  }}>
                    {selectedStory.category}
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontSize: '0.9rem', gap: '0.4rem' }}>
                    <Calendar size={15} />
                    <span>{selectedStory.date}</span>
                  </div>
                </div>

                <h2 style={{ fontSize: '2.2rem', fontWeight: 'bold', color: '#111', margin: '0 0 2rem 0', lineHeight: '1.2' }}>
                  {selectedStory.title}
                </h2>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#fed7aa', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <User size={20} color="#9a3412" />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontWeight: 'bold', color: '#111' }}>{selectedStory.author}</p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>Traveler & Writer</p>
                  </div>
                </div>

                <div style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#374151', paddingBottom: '2rem' }}>
                  <p style={{ marginBottom: '1.5rem' }}>
                    {/* The excerpt acts as a hook paragraph */}
                    <strong>{selectedStory.excerpt}</strong>
                  </p>
                  <p>
                    {selectedStory.fullStory}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Submission Form Modal */}
      <AnimatePresence>
        {showForm && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowForm(false)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }} />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }} 
              animate={{ opacity: 1, scale: 1, y: 0 }} 
              exit={{ opacity: 0, scale: 0.95, y: 20 }} 
              style={{ backgroundColor: '#fffdfa', borderRadius: '24px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', position: 'relative', zIndex: 10000, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column' }}
            >
              <button onClick={() => setShowForm(false)} style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', backgroundColor: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'background-color 0.2s', zIndex: 2 }}>
                <X size={20} color="#111" />
              </button>

              <div style={{ background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)', padding: '3rem 2rem', color: 'white', borderTopLeftRadius: '24px', borderTopRightRadius: '24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Edit3 size={32} /> Share Your Adventure
                  </h2>
                  <p style={{ fontSize: '1.1rem', opacity: 0.9, maxWidth: '600px' }}>Inspire fellow travelers by sharing your unique wildlife encounters and trekking experiences.</p>
                </div>
                <div style={{ position: 'absolute', top: '-50%', right: '-10%', width: '300px', height: '300px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%', blur: '20px' }}></div>
              </div>

              <div style={{ padding: '2.5rem 3rem' }}>
                <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Title</label>
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', top: '14px', left: '14px', color: '#9ca3af' }}><Type size={20} /></div>
                        <input required type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., A Morning with the Tigers" style={{ width: '100%', padding: '14px 14px 14px 44px', borderRadius: '12px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s', backgroundColor: '#f9fafb' }} onFocus={(e) => e.target.style.borderColor = '#ea580c'} onBlur={(e) => e.target.style.borderColor = '#e5e7eb'} />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Author Name</label>
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', top: '14px', left: '14px', color: '#9ca3af' }}><User size={20} /></div>
                        <input 
                          required 
                          type="text" 
                          name="author"
                          value={formData.author} 
                          onChange={(e) => {
                            if (/[^a-zA-Z\s]/.test(e.target.value)) return;
                            setFormData({...formData, author: e.target.value})
                          }} 
                          placeholder="Your full name" 
                          style={{ width: '100%', padding: '14px 14px 14px 44px', borderRadius: '12px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s', backgroundColor: '#f9fafb' }} 
                          onFocus={(e) => e.target.style.borderColor = '#ea580c'} 
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'} 
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Category</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', top: '14px', left: '14px', color: '#9ca3af' }}><Tag size={20} /></div>
                      <select required value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} style={{ width: '100%', padding: '14px 14px 14px 44px', borderRadius: '12px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '1rem', appearance: 'none', transition: 'border-color 0.2s', backgroundColor: '#f9fafb' }} onFocus={(e) => e.target.style.borderColor = '#ea580c'} onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}>
                        <option value="Wildlife">Wildlife</option>
                        <option value="Trekking">Trekking</option>
                        <option value="Nature">Nature</option>
                        <option value="Adventure">Adventure</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Short Excerpt</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', top: '16px', left: '14px', color: '#9ca3af' }}><FileText size={20} /></div>
                      <textarea required rows="2" value={formData.excerpt} onChange={e => setFormData({...formData, excerpt: e.target.value})} placeholder="A quick hook to capture the reader's attention..." style={{ width: '100%', padding: '14px 14px 14px 44px', borderRadius: '12px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s', resize: 'vertical', backgroundColor: '#f9fafb' }} onFocus={(e) => e.target.style.borderColor = '#ea580c'} onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}></textarea>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Full Story</label>
                    <textarea required rows="6" value={formData.fullStory} onChange={e => setFormData({...formData, fullStory: e.target.value})} placeholder="Tell us all about your incredible journey in detail..." style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s', resize: 'vertical', backgroundColor: '#f9fafb', lineHeight: '1.6' }} onFocus={(e) => e.target.style.borderColor = '#ea580c'} onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}></textarea>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Cover Image</label>
                    <div 
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      style={{ 
                        border: '2px dashed #d1d5db', 
                        borderRadius: '16px', 
                        padding: '2.5rem', 
                        textAlign: 'center', 
                        cursor: 'pointer', 
                        backgroundColor: '#f9fafb',
                        transition: 'all 0.2s',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.borderColor = '#ea580c'; e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                      onMouseOut={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    >
                      <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                      
                      {formData.image ? (
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                          <img src={formData.image} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: 0, transition: 'opacity 0.2s' }} onMouseOver={(e) => e.currentTarget.style.opacity = 1} onMouseOut={(e) => e.currentTarget.style.opacity = 0}>
                            <span style={{ color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><ImageIcon size={20} /> Change Image</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#6b7280' }}>
                          <ImageIcon size={40} color="#9ca3af" style={{ marginBottom: '1rem' }} />
                          {uploadingImage ? (
                            <span style={{ fontSize: '1.1rem', color: '#ea580c', fontWeight: 600 }}>Processing image...</span>
                          ) : (
                            <>
                              <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>Click to upload an image</span>
                              <span style={{ fontSize: '0.9rem' }}>PNG, JPG, GIF up to 5MB</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={submitting} 
                    style={{ 
                      marginTop: '1.5rem', 
                      width: '100%', 
                      padding: '16px', 
                      background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '12px', 
                      fontSize: '1.15rem', 
                      fontWeight: 'bold', 
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '0.75rem',
                      boxShadow: '0 10px 25px rgba(0,95,21,0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseOver={(e) => { if(!submitting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 15px 30px rgba(0,95,21,0.4)'; } }}
                    onMouseOut={(e) => { if(!submitting) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,95,21,0.3)'; } }}
                  >
                    {submitting ? 'Submitting...' : <><Send size={20} /> Publish Story</>}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Stories;
